// Authentication UI management

import { getAuthHeaders, getSubscriptionStatusUrl, clearTokens, refreshAccessToken, authenticatedFetch } from '../api/auth.js';
import { getPendingSearchQuery, clearPendingSearchQuery } from './signin-modal.js';
import { markSearchPerformed } from '../state/session.js';
import { capture as posthogCapture, identify as posthogIdentify, reset as posthogReset } from '../analytics/posthog.js';
import { 
    setCurrentResearchId as _setCurrentResearchId,
    clearActiveResearchIdOnServer
} from '../state/research.js';

// Callback for handleSearch - will be set from pre/legacy.js
let handleSearchCallback = null;

export function setAuthCallbacks(callbacks) {
    if (callbacks.handleSearch) {
        handleSearchCallback = callbacks.handleSearch;
    }
}

// Update UI to show signed-in state
export async function updateSignInUI(userInfo) {
    const accountIcon = document.getElementById('accountIcon');
    const accountLabel = document.getElementById('accountLabel');
    const userInfoDiv = document.getElementById('userInfo');
    const userAccountName = document.getElementById('userAccountName');
    const userAccountEmail = document.getElementById('userAccountEmail');
    const signinActionBtn = document.getElementById('signinActionBtn');
    const logoutBtn = document.getElementById('logoutBtn');
    const userAccountTier = document.getElementById('userAccountTier');
    const researchHistoryBtn = document.getElementById('researchHistoryBtn');
    const upgradeToProBtn = document.getElementById('upgradeToProBtn');
    const manageSubscriptionBtn = document.getElementById('manageSubscriptionBtn');
    
    if (accountIcon && accountLabel && userInfoDiv && userAccountName && userAccountEmail && signinActionBtn && logoutBtn) {
        // Hide share button until we confirm Pro (Pro-only feature)
        const shareResearchBtnEl = document.getElementById('shareResearchBtn');
        if (shareResearchBtnEl) shareResearchBtnEl.style.display = 'none';

        // Update accordion icon and label
        accountIcon.textContent = 'account_circle';
        accountLabel.textContent = 'Account';
        
        // Show user info and update name and email
        userInfoDiv.style.display = 'block';
        userAccountName.textContent = userInfo.name || 'User';
        userAccountEmail.textContent = userInfo.email || '';
        
        // Check subscription status and update tier label
        if (userAccountTier) {
            try {
                const response = await authenticatedFetch(getSubscriptionStatusUrl());
                
                if (response.ok) {
                    const data = await response.json();
                    if (data.status === 'success') {
                        const isPro = data.is_pro;
                        if (isPro && typeof window !== 'undefined' && window.location.search.includes('session_id=')) {
                            posthogCapture('subscription_started', { source: 'checkout_return' });
                        }
                        // Update tier label based on subscription status
                        userAccountTier.textContent = isPro ? 'Pro' : 'Free';
                        
                        // Update styling
                        if (isPro) {
                            // Pro: gradient background (existing premium-label class)
                            userAccountTier.className = 'premium-label';
                        } else {
                            // Free: teal background
                            userAccountTier.className = 'free-label';
                        }
                        
                        userAccountTier.style.display = 'block';
                        
                        // Show/hide buttons based on subscription
                        if (upgradeToProBtn) {
                            upgradeToProBtn.style.display = isPro ? 'none' : 'flex';
                        }
                        if (manageSubscriptionBtn) {
                            manageSubscriptionBtn.style.display = isPro ? 'flex' : 'none';
                        }
                        const shareResearchBtn = document.getElementById('shareResearchBtn');
                        if (shareResearchBtn) {
                            const searchBox = document.getElementById('searchBox');
                            const bottomPositioned = searchBox && searchBox.classList.contains('bottom-positioned');
                            shareResearchBtn.style.display = (isPro && bottomPositioned) ? 'flex' : 'none';
                            shareResearchBtn.classList.toggle('show', !!(isPro && bottomPositioned));
                        }
                    } else {
                        // Default to Free on error
                        userAccountTier.textContent = 'Free';
                        userAccountTier.className = 'free-label';
                        userAccountTier.style.display = 'block';
                        if (upgradeToProBtn) {
                            upgradeToProBtn.style.display = 'flex';
                        }
                        if (manageSubscriptionBtn) {
                            manageSubscriptionBtn.style.display = 'none';
                        }
                        const shareResearchBtn = document.getElementById('shareResearchBtn');
                        if (shareResearchBtn) {
                            shareResearchBtn.style.display = 'none';
                            shareResearchBtn.classList.remove('show');
                        }
                    }
                } else {
                    // Default to Free on error
                    userAccountTier.textContent = 'Free';
                    userAccountTier.className = 'free-label';
                    userAccountTier.style.display = 'block';
                    if (upgradeToProBtn) {
                        upgradeToProBtn.style.display = 'flex';
                    }
                    const shareResearchBtn = document.getElementById('shareResearchBtn');
                    if (shareResearchBtn) {
                        shareResearchBtn.style.display = 'none';
                        shareResearchBtn.classList.remove('show');
                    }
                }
            } catch (error) {
                console.error('[Auth] Error checking subscription status:', error);
                // Default to Free on error
                userAccountTier.textContent = 'Free';
                userAccountTier.className = 'free-label';
                userAccountTier.style.display = 'block';
                if (upgradeToProBtn) {
                    upgradeToProBtn.style.display = 'flex';
                }
                const shareResearchBtn = document.getElementById('shareResearchBtn');
                if (shareResearchBtn) {
                    shareResearchBtn.style.display = 'none';
                    shareResearchBtn.classList.remove('show');
                }
            }
        }
        
        // Hide sign-in button and show logout button
        signinActionBtn.style.display = 'none';
        logoutBtn.style.display = 'flex';
        
        // Show Research History and Preferences buttons for authenticated users
        if (researchHistoryBtn) {
            researchHistoryBtn.style.display = 'flex';
        }
        const preferencesBtn = document.getElementById('preferencesBtn');
        if (preferencesBtn) {
            preferencesBtn.style.display = 'flex';
        }
    }
    
    // Don't overwrite the share page's research ID — loadSharedResearch owns it on /share/* pages
    const isSharePage = window.location.pathname.startsWith('/share/');
    if (!isSharePage && userInfo && Object.prototype.hasOwnProperty.call(userInfo, 'active_research_id')) {
        if (userInfo.active_research_id) {
            console.log('[Research] Backend reports active research session:', userInfo.active_research_id);
            _setCurrentResearchId(userInfo.active_research_id);
        } else {
            _setCurrentResearchId(null);
        }
    }

    // If there's a pending search query, execute it now
    const pendingQuery = getPendingSearchQuery();
    if (pendingQuery) {
        const query = pendingQuery;
        clearPendingSearchQuery(); // Clear the pending query
        
        // Set the query in the search input
        const searchInput = document.getElementById('searchInput');
        if (searchInput) {
            searchInput.value = query;
        }
        
        // Execute the search via callback
        if (handleSearchCallback) {
            handleSearchCallback();
        } else if (window.bms && window.bms.handleSearch) {
            window.bms.handleSearch();
        }
    }
}

// Update UI to show signed-out state
export function updateSignOutUI() {
    const accountIcon = document.getElementById('accountIcon');
    const accountLabel = document.getElementById('accountLabel');
    const userInfoDiv = document.getElementById('userInfo');
    const signinActionBtn = document.getElementById('signinActionBtn');
    const logoutBtn = document.getElementById('logoutBtn');
    const researchHistoryBtn = document.getElementById('researchHistoryBtn');
    const preferencesBtn = document.getElementById('preferencesBtn');
    
    if (accountIcon && accountLabel && userInfoDiv && signinActionBtn && logoutBtn) {
        // Keep the Account label for logged out state
        accountIcon.textContent = 'account_circle';
        accountLabel.textContent = 'Account';
        
        // Hide user info
        userInfoDiv.style.display = 'none';
        const userAccountTier = document.getElementById('userAccountTier');
        if (userAccountTier) {
            // Show Free badge for non-authenticated users
            userAccountTier.textContent = 'Free';
            userAccountTier.className = 'free-label';
            userAccountTier.style.display = 'block';
        }
        
        // Show sign-in button and hide logout button
        signinActionBtn.style.display = 'flex';
        logoutBtn.style.display = 'none';
        
        // Hide Research History button for non-authenticated users
        if (researchHistoryBtn) {
            researchHistoryBtn.style.display = 'none';
        }
        
        // Hide Preferences button for non-authenticated users
        if (preferencesBtn) {
            preferencesBtn.style.display = 'none';
        }
        
        // Show Upgrade to Pro button for non-authenticated users
        const upgradeToProBtn = document.getElementById('upgradeToProBtn');
        if (upgradeToProBtn) {
            upgradeToProBtn.style.display = 'flex';
        }
        // Hide Manage Subscription button for non-authenticated users
        const manageSubscriptionBtn = document.getElementById('manageSubscriptionBtn');
        if (manageSubscriptionBtn) {
            manageSubscriptionBtn.style.display = 'none';
        }
        const shareResearchBtn = document.getElementById('shareResearchBtn');
        if (shareResearchBtn) {
            shareResearchBtn.style.display = 'none';
            shareResearchBtn.classList.remove('show');
        }
    }
    
    // Clear current research session when logged out
    _setCurrentResearchId(null);
}

// Function to check if user is authenticated
export async function checkAuthStatus() {
    try {
        let token = localStorage.getItem('access_token');
        if (!token) {
            console.log('[Auth] No access token found; trying refresh token');
            const refreshed = await refreshAccessToken();
            if (refreshed) {
                token = refreshed;
            } else {
                console.log('[Auth] No valid refresh token available');
                // Show Free badge for non-authenticated users
                const userAccountTier = document.getElementById('userAccountTier');
                if (userAccountTier) {
                    userAccountTier.textContent = 'Free';
                    userAccountTier.className = 'free-label';
                    userAccountTier.style.display = 'block';
                }
                // Show Upgrade to Pro button for non-authenticated users
                const upgradeToProBtn = document.getElementById('upgradeToProBtn');
                if (upgradeToProBtn) {
                    upgradeToProBtn.style.display = 'flex';
                }
                const shareResearchBtn = document.getElementById('shareResearchBtn');
                if (shareResearchBtn) {
                    shareResearchBtn.style.display = 'none';
                    shareResearchBtn.classList.remove('show');
                }
                return false;
            }
        }
        
        console.log('[Auth] Token found in localStorage, checking authentication status...');
        
        const response = await fetch('/api/auth/user', {
            headers: {
                'X-Access-Token': token
            }
        });
        
        if (response.ok) {
            const result = await response.json();
            if (result.status === 'success') {
                console.log('[Auth] User authenticated successfully:', {
                    email: result.user_info?.email || 'N/A',
                    name: result.user_info?.name || 'N/A',
                    active_research_id: result.user_info?.active_research_id || 'None'
                });
                updateSignInUI(result.user_info);
                const uid = result.user_info?.user_id || result.user_info?.email;
                if (uid) {
                    posthogIdentify(uid, {
                        email: result.user_info?.email,
                        name: result.user_info?.name
                    });
                }
                return true;
            }
        }
        
        // Token is invalid, try to refresh it
        console.log('[Auth] Token validation failed, attempting to refresh token');
        const newToken = await refreshAccessToken();
        if (newToken) {
            // Retry the request with new token
            const retryResponse = await fetch('/api/auth/user', {
                headers: {
                    'X-Access-Token': newToken
                }
            });
            if (retryResponse.ok) {
                const retryResult = await retryResponse.json();
                if (retryResult.status === 'success') {
                    updateSignInUI(retryResult.user_info);
                    const uid = retryResult.user_info?.user_id || retryResult.user_info?.email;
                    if (uid) {
                        posthogIdentify(uid, {
                            email: retryResult.user_info?.email,
                            name: retryResult.user_info?.name
                        });
                    }
                    return true;
                }
            }
        }
        
        // Refresh failed or retry failed, clear all tokens
        console.log('[Auth] Token refresh failed, removing tokens');
        clearTokens();
        return false;
    } catch (error) {
        console.error('[Auth] Auth check error:', error);
        return false;
    }
}

// Handle Cognito sign-in callback
export async function handleCognitoCallback() {
    try {
        // Check if we're returning from Cognito redirect
        // Require both code and state parameters to ensure valid OAuth callback
        const hasOAuthParams =
            (window.location.search.includes('code=') || window.location.hash.includes('code=')) &&
            (window.location.search.includes('state=') || window.location.hash.includes('state='));
        
        if (hasOAuthParams) {
            console.log('[Cognito] Handling sign-in callback...');
            
            // Wait for auth module to be ready
            function waitForAuthModule(callback) {
                if (window.cognitoAuth && window.cognitoAuth.handleSignInCallback) {
                    callback();
                } else {
                    // Wait for ready event or try again after delay
                    const tryAgain = () => {
                        if (window.cognitoAuth && window.cognitoAuth.handleSignInCallback) {
                            callback();
                        } else {
                            setTimeout(tryAgain, 100);
                        }
                    };
                    window.addEventListener('cognitoAuthReady', callback, { once: true });
                    setTimeout(tryAgain, 1000);
                }
            }
            
            await new Promise((resolve) => {
                waitForAuthModule(async () => {
                    try {
                        const user = await window.cognitoAuth.handleSignInCallback();
                        
                        if (user) {
                            console.log('[Cognito] Sign-in successful, user:', user);
                            
                            // Send the ID token to the backend
                            const backendResponse = await fetch('/api/auth/signin', {
                                method: 'POST',
                                headers: {
                                    'Content-Type': 'application/json'
                                },
                                body: JSON.stringify({
                                    id_token: user.id_token,
                                    access_token: user.access_token,
                                    provider: 'cognito'
                                })
                            });
                            
                            console.log('[Cognito] Backend response status:', backendResponse.status);
                            const result = await backendResponse.json();
                            console.log('[Cognito] Backend response:', result);
                            
                            if (result.status === 'success') {
                                // Store both access and refresh tokens
                                localStorage.setItem('access_token', result.access_token);
                                if (result.refresh_token) {
                                    localStorage.setItem('refresh_token', result.refresh_token);
                                }
                                console.log('[Auth] Sign-in successful! Tokens stored in localStorage');
                                console.log('[Auth] User info:', {
                                    email: result.user_info?.email || 'N/A',
                                    name: result.user_info?.name || 'N/A',
                                    active_research_id: result.user_info?.active_research_id || 'None'
                                });
                                
                                // Check if there's a pending search query from sessionStorage
                                const pendingQuery = sessionStorage.getItem('bms_pending_search_query');
                                
                                // Update UI to show signed-in state (clear pendingSearchQuery first to prevent duplicate execution)
                                const originalPendingQuery = getPendingSearchQuery();
                                clearPendingSearchQuery(); // Clear to prevent updateSignInUI from executing it
                                await updateSignInUI(result.user_info);

                                const uid = result.user_info?.user_id || result.user_info?.email;
                                if (uid) {
                                    posthogIdentify(uid, {
                                        email: result.user_info?.email,
                                        name: result.user_info?.name
                                    });
                                    posthogCapture('signed_in', { email: result.user_info?.email, provider: result.user_info?.provider });
                                }

                                console.log('[Auth] All subsequent API requests will include Authorization header with token');
                                
                                // Clean up URL
                                window.history.replaceState({}, document.title, window.location.pathname);
                                
                                // If there's a pending search, execute it instead of reloading
                                if (pendingQuery) {
                                    // Clear from sessionStorage
                                    sessionStorage.removeItem('bms_pending_search_query');
                                    markSearchPerformed(); // Mark that search will be performed
                                    
                                    // Set the query in the search input
                                    const searchInput = document.getElementById('searchInput');
                                    if (searchInput) {
                                        searchInput.value = pendingQuery;
                                    }
                                    // Execute the search via callback
                                    if (handleSearchCallback) {
                                        handleSearchCallback();
                                    } else if (window.bms && window.bms.handleSearch) {
                                        window.bms.handleSearch();
                                    }
                                } else {
                                    // Redirect back to share page if user signed in from there
                                    const postSigninRedirect = sessionStorage.getItem('bms_post_signin_redirect');
                                    if (postSigninRedirect) {
                                        sessionStorage.removeItem('bms_post_signin_redirect');
                                        window.location.href = postSigninRedirect;
                                        return;
                                    }
                                    // No pending search and no redirect — updateSignInUI (above) already
                                    // rendered name/email/Pro badge and restored active_research_id, so no
                                    // reload is needed. A reload here used to discard that render and force
                                    // a second, Stripe-latency-bound round trip through /auth/user +
                                    // /auth/subscription-status before the UI settled again.
                                }
                            } else {
                                console.error('[Cognito] Sign-in failed:', result);
                                alert('Sign-in failed: ' + result.message);
                            }
                        }
                        resolve();
                    } catch (error) {
                        // Handle "No matching state found in storage" error gracefully
                        // This can happen in iOS Safari when storage is cleared between redirects
                        if (error.message && error.message.includes('No matching state found in storage')) {
                            console.log('[Cognito] OAuth state mismatch - likely due to Safari clearing storage. Cleaning up URL and skipping callback.');
                            // Clean up URL to remove OAuth parameters
                            window.history.replaceState({}, document.title, window.location.pathname);
                            resolve();
                            return;
                        }
                        
                        console.error('[Cognito] Sign-in callback error:', error);
                        alert('Failed to complete sign-in: ' + error.message);
                        resolve();
                    }
                });
            });
        }
    } catch (error) {
        // Handle "No matching state found in storage" error gracefully
        // This can happen in iOS Safari when storage is cleared between redirects
        if (error.message && error.message.includes('No matching state found in storage')) {
            console.log('[Cognito] OAuth state mismatch - likely due to Safari clearing storage. Cleaning up URL and skipping callback.');
            // Clean up URL to remove OAuth parameters
            window.history.replaceState({}, document.title, window.location.pathname);
            return;
        }
        
        console.error('[Cognito] Sign-in callback error:', error);
        alert('Failed to complete sign-in: ' + error.message);
    }
}

// Handle logout
export async function handleLogout(resetToInitialStateCallback) {
    try {
        const token = localStorage.getItem('access_token');
        
        // Clear active research_id from server before removing token
        // This ensures the server call can use the token for authentication
        await clearActiveResearchIdOnServer();
        
        // Clear active research_id from memory
        _setCurrentResearchId(null);
        
        // Update UI to show signed-out state
        updateSignOutUI();

        posthogCapture('signed_out');
        posthogReset();

        if (token) {
            // Call logout endpoint
            await fetch('/api/auth/signout', {
                method: 'POST',
                headers: {
                    'X-Access-Token': token
                }
            });
        }
        
        // Remove tokens from localStorage
        clearTokens();
        
        // Sign out from Cognito
        if (window.cognitoAuth && window.cognitoAuth.signOutRedirect) {
            try {
                await window.cognitoAuth.signOutRedirect();
            } catch (error) {
                console.error('[Cognito] Sign-out error:', error);
                // If Cognito sign-out fails, just clear local state
                if (resetToInitialStateCallback) {
                    await resetToInitialStateCallback();
                }
            }
        } else {
            // If Cognito auth module not loaded, just clear local state
            if (resetToInitialStateCallback) {
                await resetToInitialStateCallback();
            }
        }
    } catch (error) {
        console.error('Logout error:', error);
        // Even if the request fails, clear local state and navigate to home
        // Clear active research_id from memory even on error
        _setCurrentResearchId(null);
        updateSignOutUI();
        clearTokens();
        if (resetToInitialStateCallback) {
            await resetToInitialStateCallback();
        }
    }
}
